import React from 'react';
import Head from 'next/head';
import { useRouter } from 'next/router';

import { useQuery, QueryClient, dehydrate } from 'react-query';

import { setSsrTokens } from 'utils/authentication';
import { queryClientConfig } from '@core/configs/queryClientConfig';
import { GetExpert } from 'services/experts';

import { NextSeo } from 'next-seo';
import { getExpertDynamicProps, getExpertJsonLd } from 'router/seoprops';

import { Book1, TrendUp } from 'iconsax-react';

import ExpertProfileHero from 'components/pages/experts/expertprofilehero/ExpertProfileHero';
import ExpertAbout from 'components/pages/experts/expertabout/ExpertAbout';
import ExpertSectionHeading from 'components/pages/experts/expertsectionheading/ExpertSectionHeading';
import ContentRecommendationCard from 'components/common/contentrecommendationcard/ContentRecommendationCard';
import ExploreCareerCard from 'components/pages/career/explorecareercard/ExploreCareerCard';
import Spinner from '@core/components/spinner/Spinner';

import classes from './[...slug].module.scss';

const ExpertDetailPage = ({ jsonLd, ...props }) => {
    const router = useRouter();
    const seoProps = getExpertDynamicProps({ ...props });

    const expertId = router?.query?.slug?.[0];

    const { data, isLoading } = useQuery(['get-expert', expertId], () => GetExpert(expertId), {
        enabled: !!expertId
    });

    const expert = data?.data;
    const lectures = expert?.lectures || [];
    const careerPaths = expert?.career_paths || [];
    // The design addresses the expert by their first name ("Courses by Dr. James").
    const shortName = expert?.first_name || '';

    return (
        <>
            <NextSeo {...seoProps} />
            {jsonLd && (
                <Head>
                    <script
                        type='application/ld+json'
                        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
                    />
                </Head>
            )}

            <div className={classes['page']}>
                {!isLoading && expert ? (
                    <>
                        <div className='container px-xl-0'>
                            <ExpertProfileHero expert={expert} />
                        </div>

                        <div className={classes['band']}>
                            <div className='container px-xl-0'>
                                <div className={classes['sections']}>
                                    <ExpertAbout
                                        name={shortName}
                                        description={expert.description}
                                    />

                                    {lectures.length > 0 && (
                                        <section>
                                            <ExpertSectionHeading
                                                icon={<Book1 size={20} color='#18BBA8' variant='TwoTone' />}>
                                                Courses by {shortName}
                                            </ExpertSectionHeading>
                                            <div className={classes['course-grid']}>
                                                {lectures.map((lecture) => (
                                                    <ContentRecommendationCard
                                                        key={lecture.id}
                                                        content={lecture}
                                                        hasPrice={false}
                                                    />
                                                ))}
                                            </div>
                                        </section>
                                    )}

                                    {careerPaths.length > 0 && (
                                        <section>
                                            <ExpertSectionHeading
                                                icon={<TrendUp size={20} color='#18BBA8' variant='TwoTone' />}>
                                                Career Paths by {shortName}
                                            </ExpertSectionHeading>
                                            <div className={classes['career-grid']}>
                                                {careerPaths.map((careerPath) => (
                                                    <ExploreCareerCard
                                                        key={careerPath.id}
                                                        career={careerPath}
                                                    />
                                                ))}
                                            </div>
                                        </section>
                                    )}
                                </div>
                            </div>
                        </div>
                    </>
                ) : (
                    <div className={classes['loading']}>
                        <Spinner />
                    </div>
                )}
            </div>
        </>
    );
};

export async function getServerSideProps(context) {
    setSsrTokens(context);
    const queryClient = new QueryClient(queryClientConfig);
    const expertId = context?.params?.slug?.[0];

    if (!expertId) {
        return { redirect: { permanent: false, destination: '/experts' } };
    }

    try {
        const response = await GetExpert(expertId);

        await queryClient.prefetchQuery(['get-expert', expertId], () => ({ data: response.data }));

        // Redirect to the canonical URL when the slug is missing or stale.
        let newQueryParams = '';
        const { slug, ...queryParams } = context.query;
        if (queryParams && Object.keys(queryParams).length > 0) {
            newQueryParams = `?${new URLSearchParams(queryParams).toString()}`;
        }
        const correctSlug = response.data.slug;
        if (!context?.params?.slug?.[1] || context.params.slug[1] !== correctSlug) {
            context.res.setHeader(
                'Location',
                `/experts/${expertId}/${correctSlug}${newQueryParams}`
            );
            context.res.statusCode = 302;
            context.res.end();
        }

        const fullName = [response.data.first_name, response.data.last_name]
            .filter(Boolean)
            .join(' ');

        return {
            props: {
                dehydratedState: dehydrate(queryClient),
                id: expertId,
                title: fullName,
                description: response.data.description || '',
                slug: response.data.slug || '',
                image: response.data.image || '',
                jsonLd:
                    getExpertJsonLd({
                        expert: response.data,
                        id: expertId,
                        slug: response.data.slug || ''
                    }) || null
            }
        };
    } catch (e) {
        if (e?.response?.status === 404) {
            return { redirect: { permanent: false, destination: '/404' } };
        }
        return { props: {} };
    }
}

export default ExpertDetailPage;
